import { EmptyTableState } from '@/modules/common/components/EmptyTableState';
import { getDefaultMRTOptions } from '@/modules/common/lib/react-material-table';

import { ITeam } from '@/types/team';
import { Search } from '@mui/icons-material';
import { TextField } from '@mui/material';
import {
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { getListTeamsTableColumns } from './ListTeamsTable.columns';

type ListTeamsTableProps = {
  tableOptions: Omit<MRT_TableOptions<ITeam>, 'columns'>;
  onManageLearningBudgetFunds?: (team: ITeam) => void;
  onDeleteTeam?: (team: ITeam) => void;
  onEditTeam?: (team: ITeam) => void;
  onGlobalFilterChange?: (value: string) => void;
};

const defaultMRTOptions = getDefaultMRTOptions<ITeam>();

export function ListTeamsTable({
  tableOptions,
  onManageLearningBudgetFunds,
  onDeleteTeam,
  onEditTeam,
}: ListTeamsTableProps) {
  const table = useMaterialReactTable<ITeam>({
    ...defaultMRTOptions,
    muiTableHeadProps: {
      sx: {
        display: tableOptions.data.length ? undefined : 'none',
      },
    },
    layoutMode: 'grid',
    enableGlobalFilter: true,
    manualFiltering: true,
    manualPagination: true,
    enableTopToolbar: true,
    columns: getListTeamsTableColumns({
      onManageLearningBudgetFunds,
      onDeleteTeam,
      onEditTeam,
    }),
    renderEmptyRowsFallback: ({ table }) => {
      const { columnFilters, globalFilter } = table.getState();

      const hasFilters =
        globalFilter ||
        columnFilters.some(columnFilter =>
          Array.isArray(columnFilter.value)
            ? columnFilter.value.length
            : columnFilter.value,
        );

      return (
        <EmptyTableState
          hasFilters={hasFilters}
          emptyStateImage="/images/no-teams.png"
          emptyStateTitle="Create a team"
          emptyStateMessage="Once you create a team, it will be displayed here."
        />
      );
    },
    enablePagination: !!tableOptions?.rowCount,
    renderTopToolbar: ({ table }) => {
      return (
        <nav className="flex gap-12 pb-3">
          <div className="flex-1" />
          <TextField
            id="teamSearch"
            size="small"
            slotProps={{
              input: {
                endAdornment: <Search className="ml-3" />,
                className: 'focus:outline-none',
                autoFocus: !!table.getState().globalFilter,
              },
            }}
            placeholder="Search by name..."
            value={table.getState().globalFilter ?? ''}
            onChange={event => {
              const value = event.target.value;
              table.setGlobalFilter(value);
            }}
          />
        </nav>
      );
    },
    ...tableOptions,
  });

  return <MaterialReactTable table={table} />;
}
